import algoliasearch from "algoliasearch/lite";
import instantsearch from "instantsearch.js";
import {
  connectSearchBox,
  connectSortBy,
} from "instantsearch.js/es/connectors";
import {
  configure,
  hits,
  pagination,
  stats,
} from "instantsearch.js/es/widgets";
import {
  SortByRenderState,
  SortByConnectorParams,
} from "instantsearch.js/es/connectors/sort-by/connectSortBy";

const sortByContainer: HTMLElement | null = document.querySelector("#sort-by");
const renderSortBy = (
  renderOptions: SortByRenderState & { widgetParams: SortByConnectorParams },
  isFirstRender: boolean
) => {
  const { options, currentRefinement, refine, canRefine } = renderOptions;

  if (isFirstRender) {
    const select = document.createElement("select");

    const selectArrow = sortByContainer?.querySelector("svg");
    select.classList.add(
      "font-bold",
      "text-black",
      "appearance-none",
      "hover:text-purple-500",
      "pr-6",
      "bg-transparent"
    );

    select.addEventListener("change", (event) => {
      refine((event?.target as HTMLSelectElement).value);
    });

    selectArrow?.insertAdjacentElement("beforebegin", select);
  }

  const select = sortByContainer?.querySelector("select");
  if (!select) return;

  select.disabled = !canRefine;

  select.innerHTML = `
    ${options
      .map(
        (option) => `
          <option
            value="${option.value}"
            ${option.value === currentRefinement ? "selected" : ""}
          >
            ${option.label}
          </option>
        `
      )
      .join("")}
  `;
};

const customSortBy = connectSortBy(renderSortBy);

const searchContainer = document.querySelector(
  ".js-algolia-search"
) as HTMLElement;

const initAlgoliaSearch = (searchContainer: HTMLElement): void => {
  const appId = searchContainer.getAttribute("data-app-id");
  const searchKey = searchContainer.getAttribute("data-search-key");
  let index = searchContainer.getAttribute("data-index") as string;
  let sortedByTitleIndex = searchContainer.getAttribute(
    "data-sortby-title"
  ) as string;
  let sortedByDateDescIndex = searchContainer.getAttribute(
    "data-sortby-date-desc"
  ) as string;
  let sortedByDateAscIndex = searchContainer.getAttribute(
    "data-sortby-date-asc"
  ) as string;
  if (!appId || !searchKey || !index) {
    return;
  }

  const searchClient = algoliasearch(appId, searchKey);
  const search = instantsearch({
    indexName: index,
    searchClient,
    routing: {
      stateMapping: {
        stateToRoute(uiState) {
          const indexUiState = uiState[index];
          return {
            search: indexUiState.query,
            page: indexUiState.page,
            sort: indexUiState.sortBy,
          };
        },
        routeToState(routeState) {
          return {
            [index]: {
              query: routeState.search as string,
              page: routeState.page as number,
              sortBy: routeState.sort as string,
            },
          };
        },
      },
    },
    searchFunction(helper) {
      const hitsContainer = document.querySelector("#hits") as HTMLElement;

      if (helper.state.query === "") {
        hitsContainer.classList.add("hidden");
        hitsContainer.classList.remove("contents");
        return;
      } else {
        hitsContainer.classList.remove("hidden");
        hitsContainer.classList.add("contents");
      }
      helper.search();
    },
  });

  const virtualSearchBox = connectSearchBox(() => {});

  const now = Math.floor(Date.now() / 1000);

  search.addWidgets([
    virtualSearchBox({}),
    configure({
      hitsPerPage: 7,
      filters: `expiryDate > ${now}`,
    }),
    customSortBy({
      items: [
        { label: "most relevant", value: index },
        { label: "most recent", value: sortedByDateDescIndex },
        { label: "oldest", value: sortedByDateAscIndex },
        { label: "title (A-Z)", value: sortedByTitleIndex },
      ],
    }),
    stats({
      container: "#stats",
      templates: {
        text(data, { html }) {
          return `<div class="prose"><p>${data.nbHits} results found for ${data.query}</p></div>`;
        },
      },
    }),
    pagination({
      container: "#pagination",
      cssClasses: {
        list: "pagination",
        item: "pagination__link",
        selectedItem: "pagination__link--current",
        nextPageItem: "pagination__link--arrow",
        previousPageItem: "pagination__link--arrow",
      },
      showFirst: false,
      showLast: false,
      templates: {
        next: `
        <svg width="12" height="17" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 17" fill="none" aria-label="Next">
          <path d="M2 15l7-6.314L2 2" stroke="currentColor" stroke-width="4"></path>
        </svg>
        `,
        previous: `
        <svg class="transform rotate-180" width="12" height="17" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 17" fill="none" aria-label="Previous">
          <path d="M2 15l7-6.314L2 2" stroke="currentColor" stroke-width="4"></path>
        </svg>
        `,
      },
    }),
    hits({
      container: "#hits",
      templates: {
        item(hit, { html }) {
          return html`
            <article
              class="border-t border-gray-200 py-5 md:py-9 js-nested-link hover:text-purple-500"
            >
              <p class="label mb-3 text-gray-400">
                ${hit.section === "contentStructure" ? "Content" : hit.section}
              </p>
              <a href="${hit.url}">
                <h4
                  class="headline-4 text-current transition-colors duration-200"
                >
                  ${hit.title}
                </h4>
              </a>
              <div class="prose mt-2">
                <p>${hit.referenceDescription}</p>
              </div>
            </article>
          `;
        },
      },
    }),
  ]);

  search.start();
};

if (searchContainer) {
  initAlgoliaSearch(searchContainer);
}
